import { TUser, TUserProject, EUserTitle } from 'src/types/user'
import { ReactNode, useState } from 'react'
import {
  MedicalProfessionalIcon,
  CurecallDropdownButton,
  CurecallConfirmationModal,
  GenderFemaleIcon,
  CurecallTooltip,
  GenderMaleIcon,
  AlertIcon,
  CurecallToggle,
  ECurecallSize,
  ECurecallDropdownPosition,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { TwoFactorService } from 'src/services/two-factor.service'
import { useTranslation } from 'react-i18next'
import { useNotification } from 'src/providers/notification.provider'
import { useQueryClient } from '@tanstack/react-query'
import { UserProjectService } from 'src/services/user-project.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { getErrorMessage } from 'src/utils/http.util'
import { getFormSubmitNotificationError } from './users-form.helper'
import { TModalProps, TUserProjectForm } from './users-table.component'

const ActivateCell = ({
  userProject,
  setModalProps,
  tooltipPosition,
}: {
  userProject: TUserProject
  setModalProps: (modalProps: TModalProps) => void
  tooltipPosition: 'bottom-start' | 'top-start'
}) => {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const { project } = useCurrentProject()

  const [isEnabled, setIsEnabled] = useState(userProject.isActive)

  const updateUserProject = async (data: { form: TUserProjectForm }) => {
    try {
      const updatedUserProject = await UserProjectService.updateUserProject(project.id, data.form)
      setModalProps(undefined)

      queryClient.setQueryData(['user-project-list'], (oldData: Array<TUserProject> | undefined) =>
        oldData?.map((oldUserProject) =>
          oldUserProject.UserId === updatedUserProject.UserId ? updatedUserProject : oldUserProject,
        ),
      )

      const firstname = updatedUserProject.User?.firstname
      const lastname = updatedUserProject.User?.lastname
      const userTitle = updatedUserProject.User?.title
      notify({
        appearance: 'SUCCESS',
        message: updatedUserProject.isActive
          ? t('pages.users-page.notify-user-activated-description', {
              firstname,
              lastname,
              civility: userTitle,
            })
          : t('pages.users-page.notify-user-disabled-description', {
              firstname,
              lastname,
              civility: userTitle,
            }),
        title: updatedUserProject.isActive
          ? t('pages.users-page.notify-user-activated-title')
          : t('pages.users-page.notify-user-disabled-title'),
      })
    } catch (error) {
      notify(getFormSubmitNotificationError(getErrorMessage(error), t), 10000)
    }
  }

  return (
    <CurecallTooltip
      content={
        isEnabled ? t('pages.users-page.tooltip-activate-user') : t('pages.users-page.tooltip-disable-user')
      }
      tooltipPosition={tooltipPosition}
      wrapperClassName="tw-w-max tw-mx-auto"
    >
      <CurecallToggle
        isEnabled={isEnabled}
        onChange={() => {
          setIsEnabled((prev: boolean) => !prev)
          updateUserProject({ form: { UserId: userProject.UserId, isActive: !userProject.isActive } })
        }}
      />
    </CurecallTooltip>
  )
}

const UserEmailCell = (props: {
  userEmail: string
  isEmailVerified: boolean
  tooltipLabel: string
  tooltipPosition: 'bottom-start' | 'top-start'
}) => {
  return (
    <div className="c-flex  c-gap-1.5">
      <span>{props.userEmail}</span>{' '}
      {!props.isEmailVerified && (
        <CurecallTooltip content={props.tooltipLabel} tooltipPosition={props.tooltipPosition}>
          <AlertIcon className="c-w-5 c-text-orange-700" />
        </CurecallTooltip>
      )}
    </div>
  )
}

const TitleIconCell = (props: { title: EUserTitle | undefined }) => {
  const ICON_OPTIONS: Record<EUserTitle, ReactNode> = {
    'Dr.': <MedicalProfessionalIcon />,
    'Pr.': <MedicalProfessionalIcon />,
    'Mme.': <GenderFemaleIcon />,
    'M.': <GenderMaleIcon />,
  }
  return <div className="c-w-[24px] c-mx-auto">{props.title ? ICON_OPTIONS[props.title] : undefined}</div>
}

const IdentityCell = (props: TUser) => {
  return <div>{`${props.title ?? ''} ${props.firstname} ${props.lastname}`.trim()}</div>
}

const BoldTypoCell = (text: string | undefined) => {
  return <span className="c-font-semibold">{text}</span>
}

const ActionsCell = (props: {
  menuPosition: ECurecallDropdownPosition
  updateLabel: string
  deleteLabel: string
  dropdownButtonLabel: string
  onUpdateUserClick: VoidFunction
  onDeleteUserClick: VoidFunction
  resetTwoFactorLabel?: string
  onResetTwoFactorClick?: VoidFunction
}) => {
  const items = [
    { label: props.updateLabel, onClick: props.onUpdateUserClick },
    { label: props.deleteLabel, onClick: props.onDeleteUserClick },
  ]

  if (props.resetTwoFactorLabel && props.onResetTwoFactorClick) {
    items.push({
      label: props.resetTwoFactorLabel,
      onClick: props.onResetTwoFactorClick,
    })
  }

  return (
    <CurecallDropdownButton
      dropdownButtonLabel={props.dropdownButtonLabel}
      size={ECurecallSize.SMALL}
      position={props.menuPosition}
      items={items}
    />
  )
}

const ResetTwoFactorConfirmation = (props: {
  isOpen: boolean
  userId: number
  userName: string
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const handleConfirm = async () => {
    try {
      await TwoFactorService.resetUserTwoFactor(props.userId)
      notify({
        title: t('common.actions.success'),
        message: t('pages.users-page.reset-two-factor-success', { name: props.userName }),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch {
      notify({
        title: t('common.actions.error'),
        message: t('pages.users-page.reset-two-factor-error'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      props.onClose()
    }
  }

  return (
    <CurecallConfirmationModal
      isOpen={props.isOpen}
      color="ALERT"
      onConfirm={handleConfirm}
      onCancel={props.onClose}
      title={t('pages.users-page.reset-two-factor-title')}
      cancelButtonLabel={t('common.actions.cancel')}
      confirmButtonLabel={t('common.actions.confirm')}
    >
      <p>{t('pages.users-page.reset-two-factor-description', { name: props.userName })}</p>
    </CurecallConfirmationModal>
  )
}

export {
  ActivateCell,
  UserEmailCell,
  TitleIconCell,
  IdentityCell,
  BoldTypoCell,
  ActionsCell,
  ResetTwoFactorConfirmation,
}
